import { router, setPageContent } from "..";
import {
	getSmartplugConsumption,
	getSmartplugLoad,
	getSmartplugTemperature,
} from "../api/smartplugs_{imei}";
import { lineChart } from "../device/device_log_chart";
import { locale } from "../locale";
import { state } from "../state";
import { Bulma } from "../view/bulma";
import { centeredBox } from "../view/components";

/**
 * @type {import('navigo').Handler}
 */
async function smartplugLogsPage(ctx) {
	if (!ctx || !ctx.data || !ctx.data.imei) throw "missing ctx.data.imei";
	const sp = state.smartplugs[ctx.data.imei];

	const consumptionChart = lineChart({
		label: locale.string({
			enGB: "Consumption",
			deDE: "Verbrauch",
			frFR: "Consommation",
			svSE: "Förbrukning",
		}),
		unit: "Wh",
		device: {
			type: "SmartPlug",
			serialNumber: sp.Imei,
		},
		getData: getConsumption,
	});

	const loadChart = lineChart({
		label: locale.string({
			enGB: "Load",
			deDE: "Belastung",
			frFR: "Charge",
			svSE: "Last",
		}),
		unit: "W",
		device: {
			type: "SmartPlug",
			serialNumber: sp.Imei,
		},
		getData: getLoad,
	});

	const temperatureChart = lineChart({
		label: locale.string({
			enGB: "Temperature",
			deDE: "Temperatur",
			frFR: "Température",
			svSE: "Temperatur",
		}),
		unit: "°C",
		device: {
			type: "SmartPlug",
			serialNumber: sp.Imei,
		},
		getData: getTemperature,
	});

	setPageContent(
		centeredBox(
			"large",
			Bulma.title(sp.Metadata.Name),
			Bulma.subtitle(`IMEI: ${sp.Imei}`),
			document.createElement("hr"),

			Bulma.title(
				locale.string({
					enGB: "Consumption",
					deDE: "Verbrauch",
					frFR: "Consommation",
					svSE: "Förbrukning",
				}),
				"is-5",
			),
			consumptionChart.element,
			document.createElement("hr"),

			Bulma.title(
				locale.string({
					enGB: "Load",
					deDE: "Belastung",
					frFR: "Charge",
					svSE: "Last",
				}),
				"is-5",
			),
			loadChart.element,
			document.createElement("hr"),

			Bulma.title(
				locale.string({
					enGB: "Temperature",
					deDE: "Temperatur",
					frFR: "Température",
					svSE: "Temperatur",
				}),
				"is-5",
			),
			temperatureChart.element,
			document.createElement("hr"),

			Bulma.buttons(
				Bulma.button(
					{
						onClick: () => router.jump(`/devices?id=${sp.Imei}`),
						styles: ["is-primary"],
					},
					locale.strings.back,
				),
			),
		),
	);

	consumptionChart.mount();
	loadChart.mount();
	temperatureChart.mount();

	/**
	 * @param {Date} from
	 * @param {Date} to
	 * @param {"csv"|"json"|"xlsx"} format
	 * @returns {Promise<import('../device/device_log_chart').DataPoint[]|Blob>}
	 */
	async function getConsumption(from, to, format) {
		const res = await getSmartplugConsumption(sp.Imei, {
			from: from.toISOString(),
			to: to.toISOString(),
			format: format,
		});
		if (res instanceof Blob) return res;
		else
			return res.map((log) => ({
				value: log.Consumption,
				timestamp: log.Timestamp,
			}));
	}

	/**
	 * @param {Date} from
	 * @param {Date} to
	 * @param {"csv"|"json"|"xlsx"} format
	 * @returns {Promise<import('../device/device_log_chart').DataPoint[]|Blob>}
	 */
	async function getLoad(from, to, format) {
		const res = await getSmartplugLoad(sp.Imei, {
			from: from.toISOString(),
			to: to.toISOString(),
			format: format,
		});

		if (res instanceof Blob) return res;
		else
			return res.map((log) => ({
				value: log.Load,
				timestamp: log.Timestamp,
			}));
	}

	/**
	 * @param {Date} from
	 * @param {Date} to
	 * @param {"csv"|"json"|"xlsx"} format
	 * @returns {Promise<import('../device/device_log_chart').DataPoint[]|Blob>}
	 */
	async function getTemperature(from, to, format) {
		const res = await getSmartplugTemperature(sp.Imei, {
			from: from.toISOString(),
			to: to.toISOString(),
			format: format,
		});

		if (res instanceof Blob) return res;
		else
			return res.map((log) => ({
				value: log.Temperature,
				timestamp: log.Timestamp,
			}));
	}
}

export { smartplugLogsPage };
